// [ai] Pages that carry their own E2E seam and keyboard entries register here while mounted: window.__dossier.pages.<name>
// holds the page's actions, dump().<name> its state, and the ? sheet lists its keys under the KEYMAP. Gone on unmount.
import { useEffect, useRef } from "react";
export type PageSeam = { dump: () => unknown; keys?: [string, string][] } & Record<string, unknown>;
export const PAGES = new Map<string, PageSeam>();
// [ai] The window seam reads pages.<name>: a Proxy over the map, so a page's actions are reachable by name from
// the console and the E2E scripts while it is mounted, and undefined once it unmounts.
export const pages: Record<string, PageSeam | undefined> = new Proxy({} as Record<string, PageSeam | undefined>, {
  get: (_t, k) => (typeof k === "string" ? PAGES.get(k) : undefined), has: (_t, k) => typeof k === "string" && PAGES.has(k), ownKeys: () => [...PAGES.keys()],
  getOwnPropertyDescriptor: (_t, k) => (typeof k === "string" && PAGES.has(k) ? { value: PAGES.get(k), enumerable: true, configurable: true } : undefined),
});
export function pageDumps(): Record<string, unknown> { return Object.fromEntries([...PAGES].map(([k, s]) => [k, s.dump()])); }
export function pageKeys(): [string, string][] { return [...PAGES.values()].flatMap(s => s.keys ?? []); }
export function usePageSeam(name: string, seam: PageSeam): void {
  const ref = useRef(seam); ref.current = seam;
  useEffect(() => {
    // the registered object delegates to the latest render's closures, so the seam never goes stale
    const live: PageSeam = { dump: () => ref.current.dump() };
    for (const k of Object.keys(ref.current)) { const v = ref.current[k]; live[k] = typeof v === "function" ? (...a: unknown[]) => (ref.current[k] as (...a: unknown[]) => unknown)(...a) : v; }
    PAGES.set(name, live); return () => { if (PAGES.get(name) === live) PAGES.delete(name); };
  }, [name]);
}
